'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface ZonaFisica {
  id: string;
  nome: string;
  tipo: string;
  piano: number;
  edificio: string;
}

interface SensoreAmbientale {
  id: string;
  nome: string;
  tipo: string;
  stato: string;
  zona_id: string;
  posizione: string;
  soglia_temp_min: number;
  soglia_temp_max: number;
  soglia_umidita_min: number;
  soglia_umidita_max: number;
  ultima_lettura: string;
  zona?: ZonaFisica;
}

interface LetturaStorico {
  timestamp: string;
  temperatura: number | null;
  umidita: number | null;
}

interface AlertAmbientale {
  id: string;
  titolo: string;
  descrizione: string;
  livello: string;
  stato: string;
  created_at: string;
}

const STATO_COLORS: Record<string, string> = {
  online: 'text-emerald-400',
  offline: 'text-gray-400',
  allarme: 'text-red-400',
  warning: 'text-amber-400',
  manutenzione: 'text-blue-400',
};

const STATO_BG: Record<string, string> = {
  online: 'bg-emerald-500/20 text-emerald-400',
  offline: 'bg-gray-500/20 text-gray-400',
  allarme: 'bg-red-500/20 text-red-400',
  warning: 'bg-amber-500/20 text-amber-400',
  manutenzione: 'bg-blue-500/20 text-blue-400',
};

const LIVELLO_BG: Record<string, string> = {
  info: 'bg-sky-500/20 text-sky-400',
  warning: 'bg-amber-500/20 text-amber-400',
  critico: 'bg-red-500/20 text-red-400',
  emergenza: 'bg-red-700/30 text-red-300',
};

const ZONA_TIPO_LABEL: Record<string, string> = {
  sala_server: 'Sala Server',
  laboratorio: 'Laboratorio',
  ufficio: 'Ufficio',
  corridoio: 'Corridoio',
  magazzino: 'Magazzino',
  sala_ced: 'Sala CED',
  altro: 'Altro',
};

export default function MonitoraggioAmbientaleContent() {
  const [zone, setZone] = useState<ZonaFisica[]>([]);
  const [sensori, setSensori] = useState<SensoreAmbientale[]>([]);
  const [alerts, setAlerts] = useState<AlertAmbientale[]>([]);
  const [selectedSensore, setSelectedSensore] = useState<string | null>(null);
  const [storico, setStorico] = useState<LetturaStorico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ultimeLettureMap, setUltimeLettureMap] = useState<Record<string, { temperatura: number | null; umidita: number | null }>>({});
  const [showAddSensore, setShowAddSensore] = useState(false);
  const [editSensore, setEditSensore] = useState<SensoreAmbientale | null>(null);
  const [saving, setSaving] = useState(false);
  const [formSensore, setFormSensore] = useState({ nome: '', tipo: 'temperatura_umidita', zona_id: '', posizione: '', soglia_temp_min: 18, soglia_temp_max: 27, soglia_umidita_min: 30, soglia_umidita_max: 70 });

  const fetchData = useCallback(async () => {
    try {
      const [zoneRes, sensoriRes, alertsRes] = await Promise.all([
        supabase.from('zone_fisiche').select('*').order('nome'),
        supabase.from('sensori_ambientali').select('*, zona:zone_fisiche(id,nome,tipo,piano,edificio)').order('nome'),
        supabase.from('alert_ambientali').select('*').in('stato', ['attivo', 'riconosciuto']).order('created_at', { ascending: false }).limit(10),
      ]);

      if (zoneRes.error) throw zoneRes.error;
      if (sensoriRes.error) throw sensoriRes.error;
      if (alertsRes.error) throw alertsRes.error;

      setZone(zoneRes.data || []);
      setSensori(sensoriRes.data || []);
      setAlerts(alertsRes.data || []);

      // Fetch ultima lettura per ogni sensore
      const sensoriIds = (sensoriRes.data || []).map((s: SensoreAmbientale) => s.id);
      if (sensoriIds.length > 0) {
        const map: Record<string, { temperatura: number | null; umidita: number | null }> = {};
        for (const sid of sensoriIds) {
          const { data: lettura } = await supabase
            .from('letture_sensori')
            .select('temperatura, umidita')
            .eq('sensore_id', sid)
            .order('timestamp', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (lettura) map[sid] = { temperatura: lettura.temperatura, umidita: lettura.umidita };
        }
        setUltimeLettureMap(map);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStorico = useCallback(async (sensoreId: string) => {
    const { data, error: err } = await supabase
      .from('letture_sensori')
      .select('timestamp, temperatura, umidita')
      .eq('sensore_id', sensoreId)
      .order('timestamp', { ascending: true })
      .limit(50);
    if (!err && data) {
      setStorico(data.map(d => ({
        timestamp: new Date(d.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
        temperatura: d.temperatura,
        umidita: d.umidita,
      })));
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (selectedSensore) fetchStorico(selectedSensore);
  }, [selectedSensore, fetchStorico]);

  const riconosciAlert = async (alertId: string) => {
    await supabase.from('alert_ambientali').update({ stato: 'riconosciuto', riconosciuto_at: new Date().toISOString() }).eq('id', alertId);
    fetchData();
  };

  const getTempColor = (temp: number | null | undefined, min: number, max: number) => {
    if (temp === null || temp === undefined) return 'text-gray-400';
    if (temp > max) return 'text-red-400';
    if (temp < min) return 'text-blue-400';
    return 'text-emerald-400';
  };

  const getTempBg = (temp: number | null | undefined, min: number, max: number) => {
    if (temp === null || temp === undefined) return 'bg-gray-800';
    if (temp > max) return 'bg-red-900/40 border border-red-500/30';
    if (temp < min) return 'bg-blue-900/40 border border-blue-500/30';
    return 'bg-emerald-900/20 border border-emerald-500/20';
  };

  const alertAttivi = alerts.filter(a => a.stato === 'attivo').length;
  const sensoriOnline = sensori.filter(s => s.stato === 'online').length;
  const sensoriAllarme = sensori.filter(s => s.stato === 'allarme').length;

  const openAddSensore = () => {
    setEditSensore(null);
    setFormSensore({ nome: '', tipo: 'temperatura_umidita', zona_id: '', posizione: '', soglia_temp_min: 18, soglia_temp_max: 27, soglia_umidita_min: 30, soglia_umidita_max: 70 });
    setShowAddSensore(true);
  };

  const openEditSensore = (s: SensoreAmbientale) => {
    setEditSensore(s);
    setFormSensore({ nome: s.nome, tipo: s.tipo, zona_id: s.zona_id || '', posizione: s.posizione || '', soglia_temp_min: s.soglia_temp_min, soglia_temp_max: s.soglia_temp_max, soglia_umidita_min: s.soglia_umidita_min, soglia_umidita_max: s.soglia_umidita_max });
    setShowAddSensore(true);
  };

  const handleSaveSensore = async () => {
    if (!formSensore.nome.trim()) { setError('Nome sensore obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...formSensore, zona_id: formSensore.zona_id || null, stato: 'online' };
      if (editSensore) {
        const { error: err } = await supabase.from('sensori_ambientali').update({ nome: formSensore.nome, tipo: formSensore.tipo, zona_id: formSensore.zona_id || null, posizione: formSensore.posizione, soglia_temp_min: formSensore.soglia_temp_min, soglia_temp_max: formSensore.soglia_temp_max, soglia_umidita_min: formSensore.soglia_umidita_min, soglia_umidita_max: formSensore.soglia_umidita_max }).eq('id', editSensore.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('sensori_ambientali').insert(payload);
        if (err) throw err;
      }
      setShowAddSensore(false);
      setEditSensore(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard Ambientale</h1>
            <p className="text-muted-color text-sm mt-1">Monitoraggio temperatura, umidità e infrastruttura fisica</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            <button onClick={openAddSensore} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
              <Icon name="PlusIcon" size={16} />
              Aggiungi Sensore
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
                <Icon name="SignalIcon" size={20} className="text-sky-400" />
              </div>
              <div>
                <p className="text-muted-color text-xs">Sensori Totali</p>
                <p className="text-2xl font-bold text-white">{sensori.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={20} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-muted-color text-xs">Online</p>
                <p className="text-2xl font-bold text-emerald-400">{sensoriOnline}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Icon name="ExclamationTriangleIcon" size={20} className="text-red-400" />
              </div>
              <div>
                <p className="text-muted-color text-xs">In Allarme</p>
                <p className="text-2xl font-bold text-red-400">{sensoriAllarme}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Icon name="BellAlertIcon" size={20} className="text-amber-400" />
              </div>
              <div>
                <p className="text-muted-color text-xs">Alert Attivi</p>
                <p className="text-2xl font-bold text-amber-400">{alertAttivi}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Alert Banner */}
        {alertAttivi > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ExclamationTriangleIcon" size={18} className="text-red-400" />
              <span className="text-red-400 font-semibold text-sm">Alert Attivi ({alertAttivi})</span>
            </div>
            {alerts.filter(a => a.stato === 'attivo').map(alert => (
              <div key={alert.id} className="flex items-center justify-between bg-surface rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${LIVELLO_BG[alert.livello] || 'bg-gray-500/20 text-gray-400'}`}>
                    {alert.livello.toUpperCase()}
                  </span>
                  <div>
                    <p className="text-white text-sm font-semibold">{alert.titolo}</p>
                    <p className="text-muted-color text-xs">{alert.descrizione}</p>
                  </div>
                </div>
                <button onClick={() => riconosciAlert(alert.id)} className="text-xs px-3 py-1 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors">
                  Riconosci
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Mappa visiva temperature per zona */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Mappa Temperature per Zona</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {zone.map(zona => {
              const sensoriZona = sensori.filter(s => s.zona_id === zona.id);
              return (
                <div key={zona.id} className="bg-surface rounded-xl border border-border p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-semibold text-sm">{zona.nome}</p>
                      <p className="text-muted-color text-xs">{ZONA_TIPO_LABEL[zona.tipo] || zona.tipo} — Piano {zona.piano}</p>
                    </div>
                    <span className="text-xs text-muted-color">{sensoriZona.length} sensori</span>
                  </div>
                  <div className="space-y-2">
                    {sensoriZona.length === 0 && (
                      <p className="text-muted-color text-xs text-center py-2">Nessun sensore</p>
                    )}
                    {sensoriZona.map(s => {
                      const lettura = ultimeLettureMap[s.id];
                      const temp = lettura?.temperatura ?? null;
                      const umid = lettura?.umidita ?? null;
                      return (
                        <div
                          key={s.id}
                          onClick={() => setSelectedSensore(selectedSensore === s.id ? null : s.id)}
                          className={`rounded-lg p-3 cursor-pointer transition-all ${getTempBg(temp, s.soglia_temp_min, s.soglia_temp_max)} ${selectedSensore === s.id ? 'ring-2 ring-sky-500' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white text-xs font-semibold">{s.nome}</p>
                              <p className="text-muted-color text-xs">{s.posizione}</p>
                            </div>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATO_BG[s.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                              {s.stato}
                            </span>
                          </div>
                          <div className="flex gap-4 mt-2">
                            {temp !== null && (
                              <div className="flex items-center gap-1">
                                <Icon name="FireIcon" size={12} className={getTempColor(temp, s.soglia_temp_min, s.soglia_temp_max)} />
                                <span className={`text-sm font-bold ${getTempColor(temp, s.soglia_temp_min, s.soglia_temp_max)}`}>{temp.toFixed(1)}°C</span>
                              </div>
                            )}
                            {umid !== null && (
                              <div className="flex items-center gap-1">
                                <Icon name="CloudIcon" size={12} className="text-sky-400" />
                                <span className="text-sm font-bold text-sky-400">{umid.toFixed(1)}%</span>
                              </div>
                            )}
                            {temp === null && umid === null && (
                              <span className="text-muted-color text-xs">Nessuna lettura</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Grafico storico */}
        {selectedSensore && storico.length > 0 && (
          <div className="bg-surface rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">
                Storico — {sensori.find(s => s.id === selectedSensore)?.nome}
              </h2>
              <button onClick={() => setSelectedSensore(null)} className="text-muted-color hover:text-white transition-colors">
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={storico}>
                <defs>
                  <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="umidGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="timestamp" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Area type="monotone" dataKey="temperatura" name="Temp (°C)" stroke="#f97316" fill="url(#tempGrad)" strokeWidth={2} dot={false} connectNulls />
                <Area type="monotone" dataKey="umidita" name="Umidità (%)" stroke="#38bdf8" fill="url(#umidGrad)" strokeWidth={2} dot={false} connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Add/Edit Sensore Modal */}
        {showAddSensore && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-white">{editSensore ? 'Modifica Sensore' : 'Aggiungi Sensore'}</h3>
                <button onClick={() => { setShowAddSensore(false); setEditSensore(null); }} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                    <input value={formSensore.nome} onChange={e => setFormSensore(p => ({ ...p, nome: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Tipo</label>
                    <select value={formSensore.tipo} onChange={e => setFormSensore(p => ({ ...p, tipo: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="temperatura">Temperatura</option>
                      <option value="umidita">Umidità</option>
                      <option value="temperatura_umidita">Temperatura + Umidità</option>
                      <option value="co2">CO2</option>
                      <option value="fumo">Fumo</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                    <select value={formSensore.zona_id} onChange={e => setFormSensore(p => ({ ...p, zona_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Zona —</option>
                      {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Posizione</label>
                    <input value={formSensore.posizione} onChange={e => setFormSensore(p => ({ ...p, posizione: e.target.value }))} placeholder="es. Rack A, Parete Nord" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Soglia Temp Min (°C)</label>
                    <input type="number" step="0.5" value={formSensore.soglia_temp_min} onChange={e => setFormSensore(p => ({ ...p, soglia_temp_min: parseFloat(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Soglia Temp Max (°C)</label>
                    <input type="number" step="0.5" value={formSensore.soglia_temp_max} onChange={e => setFormSensore(p => ({ ...p, soglia_temp_max: parseFloat(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Soglia Umidità Min (%)</label>
                    <input type="number" min="0" max="100" value={formSensore.soglia_umidita_min} onChange={e => setFormSensore(p => ({ ...p, soglia_umidita_min: parseInt(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Soglia Umidità Max (%)</label>
                    <input type="number" min="0" max="100" value={formSensore.soglia_umidita_max} onChange={e => setFormSensore(p => ({ ...p, soglia_umidita_max: parseInt(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => { setShowAddSensore(false); setEditSensore(null); }} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                <button onClick={handleSaveSensore} disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                  {saving ? 'Salvataggio...' : editSensore ? 'Aggiorna' : 'Aggiungi'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sensori Grid - add edit button */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sensori.map(s => {
            const lettura = ultimeLettureMap[s.id];
            const tempColor = getTempColor(lettura?.temperatura, s.soglia_temp_min, s.soglia_temp_max);
            const tempBg = getTempBg(lettura?.temperatura, s.soglia_temp_min, s.soglia_temp_max);
            return (
              <div key={s.id} className={`bg-surface rounded-xl border p-5 ${s.stato === 'allarme' ? 'border-red-500/50' : s.stato === 'warning' ? 'border-amber-500/40' : 'border-border'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-semibold">{s.nome}</p>
                    <p className="text-muted-color text-xs">{s.zona?.nome || '—'} · {s.posizione || '—'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATO_BG[s.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                      {s.stato}
                    </span>
                    <button onClick={() => openEditSensore(s)} className="p-1 rounded hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors" title="Modifica soglie">
                      <Icon name="PencilIcon" size={12} />
                    </button>
                  </div>
                </div>
                {lettura ? (
                  <div className="grid grid-cols-2 gap-2">
                    {lettura.temperatura !== null && (
                      <div className={`rounded-lg p-3 text-center ${tempBg}`}>
                        <p className="text-muted-color text-xs mb-1">Temperatura</p>
                        <p className={`text-xl font-bold ${tempColor}`}>{lettura.temperatura.toFixed(1)}°C</p>
                        <p className="text-xs text-muted-color">{s.soglia_temp_min}–{s.soglia_temp_max}°C</p>
                      </div>
                    )}
                    {lettura.umidita !== null && (
                      <div className="bg-surface-elevated rounded-lg p-3 text-center">
                        <p className="text-muted-color text-xs mb-1">Umidità</p>
                        <p className="text-xl font-bold text-sky-400">{lettura.umidita.toFixed(0)}%</p>
                        <p className="text-xs text-muted-color">{s.soglia_umidita_min}–{s.soglia_umidita_max}%</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-color text-xs">Nessuna lettura disponibile</div>
                )}
                <button onClick={() => setSelectedSensore(s.id === selectedSensore ? null : s.id)} className="mt-3 w-full text-xs px-3 py-1.5 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors">
                  {s.id === selectedSensore ? 'Nascondi storico' : 'Vedi storico'}
                </button>
              </div>
            );
          })}
          {sensori.length === 0 && (
            <div className="col-span-3 text-center py-12 text-muted-color">
              <Icon name="SignalIcon" size={48} className="mx-auto mb-3 opacity-30" />
              <p>Nessun sensore configurato</p>
              <button onClick={openAddSensore} className="mt-3 text-sky-400 hover:text-sky-300 text-sm font-semibold">+ Aggiungi il primo sensore</button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
